import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DbService from "../services/DbService";
import { Chip } from "@mui/material";
import ActionsCell from "./actionsCell";
import { enqueueSnackbar } from "notistack";
import {
  createTeacher,
  deleteTeacher,
  updateTeacher,
} from "../services/teacherService";
import { Teacher } from "../types/TeacherModel"; // Updated import path
import { TeacherForm } from "./forms/TeacherForm";
import { useAdmin } from "../context/AdminContext";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";
import EmailAvatar from "./EmailAvatar";

const TeacherList: React.FC = () => {
  const handleError = useHandleError();

  const {
    teachers,
    schools,
    setTeachersToAdminProvider,
    refreshTeacherList,
    setSchoolsToAdminProvider,
  } = useAdmin();

  const [loading, setLoading] = useState<boolean>(true);

  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | undefined>(
    undefined
  );

  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null);

  const [hasSchoolsChanged, setHasSchoolsChanged] = useState<boolean>(false);

  const hasFetchedRef = React.useRef(false);
  const hasFetchedSchoolsRef = React.useRef(false);

  const columns: GridColDef[] = [
    {
      field: "isActive",
      headerName: "Status",
      width: 90,
      renderCell: (params) => {
        const color = params.value === true ? "success" : "default";
        const text = params.value === true ? "ACTIVE" : "DISABLED";
        return (
          <Chip
            style={{ fontSize: ".7rem" }}
            label={text}
            size="small"
            color={color}
          />
        );
      },
    },
    { field: "name", headerName: "Name", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "mobile", headerName: "Mobile", width: 200 },
    {
      field: "city",
      headerName: "City",
      width: 130,
      valueGetter: (params) => {
        return params.row?.city;
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      valueGetter: (params) => {
        return params.row?.country;
      },
    },
    { field: "schoolName", headerName: "School", width: 200 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }
        const updatedBy = params.row["updatedBy"];
        const date = new Date(params.value);
        const formattedDate = date.toLocaleDateString("en-AU");

        return (
          <div
            style={{
              display: "flex",
              gap: ".25rem",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{formattedDate}</span>
            <EmailAvatar email={updatedBy}></EmailAvatar>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row: Teacher = params.row as Teacher;

        return (
          <ActionsCell
            onEdit={() => handleEditTeacher(row)}
            onDelete={() => handleDeleteTeacher(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchTeachers = async () => {
      setLoading(true);
      try {
        const response = await DbService.getTeachers();
        setTeachersToAdminProvider(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedRef.current) {
      fetchTeachers();
      hasFetchedRef.current = true;
    }

    ///////fetch schools for

    const fetchSchools = async () => {
      setLoading(true);
      try {
        const response = await DbService.getSchools();
        setSchoolsToAdminProvider(response.data);
      } catch (err: any) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedSchoolsRef.current) {
      fetchSchools();
      hasFetchedSchoolsRef.current = true;
    }
  }, [handleError, setTeachersToAdminProvider]);

  const handleCloseDialog = () => {
    if (hasSchoolsChanged) {
      refreshList();
    }
    setSelectedTeacher(undefined);
  };

  const handleEditTeacher = (row: Teacher) => {
    setSelectedTeacher(row);
  };

  const handleTeacherSubmit = async (teacherData: Teacher) => {
    if (teacherData._id) {
      handleUpdateTeacher(teacherData);
    } else {
      try {
        const response = await createTeacher(teacherData);
        enqueueSnackbar(response.message, {
          variant: "success",
        });

        setSelectedTeacher(teacherData);
        setHasSchoolsChanged(true);
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleUpdateTeacher = async (teacherData: Teacher) => {
    if (!teacherData._id) {
      return;
    }
    try {
      const response = await updateTeacher(teacherData._id, teacherData);

      setSelectedTeacher(response.data);

      enqueueSnackbar(response.message, {
        variant: "success",
      });
      setHasSchoolsChanged(true);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDeleteTeacher = async (row: Teacher) => {
    if (!row?._id) {
      enqueueSnackbar(`Id is missing from this ${row.name}`, {
        variant: "error",
      });
      return;
    }
    setTeacherToDelete(row);
  };

  const handleDeleteTeacherConfirm = async (
    row: Teacher | null | undefined
  ) => {
    if (row?._id) {
      try {
        const response = await deleteTeacher(row._id, row.email);
        enqueueSnackbar(response.message, {
          variant: "success",
        });
        refreshList();
      } catch (error) {
        handleError(error);
      } finally {
        setTeacherToDelete(null);
      }
    }
  };

  const refreshList = async () => {
    setLoading(true);
    try {
      await refreshTeacherList();
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
      setHasSchoolsChanged(false);
    }
  };

  return (
    <DynamicTableListTemplate<Teacher>
      columns={columns}
      data={teachers}
      loading={loading}
      selectedRow={selectedTeacher}
      FormComponent={
        <TeacherForm
          schools={schools} // Updated to use schools
          selectedRow={selectedTeacher}
          onSubmit={handleTeacherSubmit}
        />
      }
      entityLabel="Teacher"
      dialogTitle="Teacher Entry Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeleteTeacherConfirm}
      entityToDelete={teacherToDelete}
    />
  );
};

export default TeacherList;
